import { useEffect, useRef } from "react";
import { useAssets } from "@/contexts/AssetContext.jsx";
import { useDowntime } from "@/contexts/DowntimeContext.jsx";

/**
 * Component that enriches assets with downtime data once both are loaded,
 * since now Asset might be able to get last fault information from Downtime.
 * This component must be nested inside both AssetProvider and DowntimeProvider.
 * @returns {null} This component doesn't render anything, it just orchestrates data.
 */
export function AssetDowntimeEnricher() {
  const {
    assets,
    enrichWithDowntime,
    lastUpdatedTime: assetsLastUpdatedTime,
  } = useAssets();
  const { downtimeEvents, lastUpdatedTime } = useDowntime();
  const lastProcessedTimeRef = useRef(null);
  const enrichWithDowntimeRef = useRef(enrichWithDowntime);
  const hasEnrichedForCurrentAssetsRef = useRef(false);
  const lastAssetsUpdatedTimeRef = useRef(null);

  // Keep the function ref in sync
  useEffect(() => {
    enrichWithDowntimeRef.current = enrichWithDowntime;
  }, [enrichWithDowntime]);

  // This useEffect tracks when assets are refetched & sets up for enrichment if needed.
  useEffect(() => {
    if (assetsLastUpdatedTime) {
      const assetsTime = assetsLastUpdatedTime.getTime();
      const assetsWereRefetched =
        lastAssetsUpdatedTimeRef.current !== null
        && lastAssetsUpdatedTimeRef.current !== assetsTime;

      // When assets are refetched, reset the processed time to allow re-enrichment.
      if (assetsWereRefetched || lastAssetsUpdatedTimeRef.current === null) {
        lastProcessedTimeRef.current = null;
        hasEnrichedForCurrentAssetsRef.current = false;

        // If downtime is already available, trigger enrichment immediately.
        if (assets.length > 0 && downtimeEvents.length > 0 && lastUpdatedTime) {
          const downtimeTime = lastUpdatedTime.getTime();
          lastProcessedTimeRef.current = downtimeTime;
          enrichWithDowntimeRef.current(downtimeEvents, lastUpdatedTime);
        }
      }

      lastAssetsUpdatedTimeRef.current = assetsTime;
    }
  }, [
    assetsLastUpdatedTime,
    assets.length,
    downtimeEvents.length,
    lastUpdatedTime,
  ]);

  // Whenever downtime is updated, the assets should be set up to be enriched.
  useEffect(() => {
    if (assets.length > 0 && downtimeEvents.length > 0 && lastUpdatedTime) {
      const downtimeTime = lastUpdatedTime.getTime();

      // Enrich if this is a new downtime update OR if assets were refetched (lastProcessedTimeRef is null)
      // This prevents enrichment when downtime is filtered locally, but allows it when assets are refetched.
      if (
        lastProcessedTimeRef.current === null
        || lastProcessedTimeRef.current !== downtimeTime
      ) {
        lastProcessedTimeRef.current = downtimeTime;
        hasEnrichedForCurrentAssetsRef.current = false;
        enrichWithDowntimeRef.current(downtimeEvents, lastUpdatedTime);
      }
    }
    // Specifically only depend on lastUpdatedTime - this only changes when downtime is refreshed from API.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUpdatedTime?.getTime()]);

  // When assets are first loaded check to see if downtime is already available, and if so, set up to enrich the assets.
  useEffect(() => {
    if (
      assets.length > 0
      && downtimeEvents.length > 0
      && lastUpdatedTime
      && !hasEnrichedForCurrentAssetsRef.current
    ) {
      const downtimeTime = lastUpdatedTime.getTime();

      // Enrich if we haven't processed this downtime yet OR if assets were refetched
      if (
        lastProcessedTimeRef.current === null
        || lastProcessedTimeRef.current !== downtimeTime
      ) {
        lastProcessedTimeRef.current = downtimeTime;
        hasEnrichedForCurrentAssetsRef.current = true;
        enrichWithDowntimeRef.current(downtimeEvents, lastUpdatedTime);
      }
    }
    // Specifically only depend on assets.length to trigger when assets first load or are refetched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets.length]);

  return null;
}
